import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tooltip,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { wardrobeAPI } from '../services/api';
import { ItemImage } from '../types';
import { useUI } from '../contexts/UIContext';

interface ItemImageGalleryProps {
  itemId: number;
  images: ItemImage[];
  primaryImageId?: string;
  onImagesUpdate: (images: ItemImage[], primaryImageId?: string) => void;
  editable?: boolean;
}

const ItemImageGallery: React.FC<ItemImageGalleryProps> = ({
  itemId,
  images,
  primaryImageId,
  onImagesUpdate,
  editable = false,
}) => {
  const { confirm } = useUI();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Sort images by order_index
  const sortedImages = [...images].sort((a, b) => a.order_index - b.order_index);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    setUploading(true);
    setError(null);

    // Upload each file in sequence so the backend can promote a legacy
    // image_url → images[0] on the first upload, then append the rest in
    // order. Parallel upload would race the legacy-migration and double-add
    // the legacy entry. Each successful upload also returns the
    // server-canonical images array via response.data — we use only the
    // first response's primary id and accumulate locally otherwise.
    const accumulated = [...images];
    let nextPrimary = primaryImageId;
    let firstError: string | null = null;
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await wardrobeAPI.addImage(itemId, formData);
        const newImage = response.data.image;
        accumulated.push(newImage);
        if (!nextPrimary) nextPrimary = newImage.image_id;
      } catch (err: any) {
        firstError = firstError || err.response?.data?.error || `Failed to upload ${file.name}`;
        // Keep going — partial success is better than aborting on one bad file.
      }
    }
    onImagesUpdate(accumulated, nextPrimary);
    if (firstError) setError(firstError);
    setUploading(false);
  }, [itemId, images, primaryImageId, onImagesUpdate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.heic']
    },
    multiple: true,  // user explicitly wanted multi-image upload
    disabled: !editable || uploading,
  });

  const handleDeleteImage = async (imageId: string) => {
    if (images.length === 1) {
      setError('Cannot delete the last image');
      return;
    }

    const ok = await confirm({
      title: 'Delete this image?',
      message: 'It will be removed from this item permanently.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    setLoading(true);
    setError(null);

    try {
      await wardrobeAPI.deleteImage(itemId, imageId);

      // Update images
      const updatedImages = images.filter(img => img.image_id !== imageId);

      // Update primary image if necessary
      let newPrimaryId = primaryImageId;
      if (primaryImageId === imageId && updatedImages.length > 0) {
        newPrimaryId = updatedImages[0].image_id;
      }

      onImagesUpdate(updatedImages, newPrimaryId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete image');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (imageId: string) => {
    setLoading(true);
    setError(null);

    try {
      await wardrobeAPI.setPrimaryImage(itemId, imageId);

      // Update images array to reflect new primary
      const updatedImages = images.map(img => ({
        ...img,
        is_primary: img.image_id === imageId
      }));

      onImagesUpdate(updatedImages, imageId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set primary image');
    } finally {
      setLoading(false);
    }
  };

  const getPrimaryImage = () => {
    if (primaryImageId) {
      const primary = images.find(img => img.image_id === primaryImageId);
      if (primary) return primary;
    }
    return images.find(img => img.is_primary) || images[0];
  };

  const primaryImage = getPrimaryImage();

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Primary Image Display */}
      {primaryImage && (
        <Card sx={{ mb: 2 }}>
          <CardMedia
            component="img"
            image={primaryImage.image_url}
            alt="Primary image"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: 400,
              objectFit: 'contain',
              backgroundColor: '#f5f5f5',
              cursor: 'pointer',
            }}
            onClick={() => setPreviewImage(primaryImage.image_url)}
          />
        </Card>
      )}

      {/* Image Gallery Grid */}
      <Typography variant="subtitle2" gutterBottom>
        All Images ({images.length})
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1,
          mb: 2,
        }}
      >
        {sortedImages.map((image) => (
          <Box key={image.image_id}>
            <Card
              sx={{
                position: 'relative',
                border: image.image_id === primaryImage?.image_id ? '2px solid' : 'none',
                borderColor: 'primary.main',
              }}
            >
              <CardMedia
                component="img"
                image={image.image_url}
                alt={`Image ${image.order_index + 1}`}
                sx={{
                  height: 120,
                  objectFit: 'cover',
                  cursor: 'pointer',
                }}
                onClick={() => setPreviewImage(image.image_url)}
              />

              {/* Primary indicator */}
              {image.image_id === primaryImage?.image_id && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                  }}
                >
                  Primary
                </Box>
              )}

              {/* Action Buttons */}
              {editable && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    display: 'flex',
                    gap: 0.5,
                  }}
                >
                  <Tooltip title={image.is_primary ? "Primary image" : "Set as primary"}>
                    <IconButton
                      size="small"
                      onClick={() => handleSetPrimary(image.image_id)}
                      disabled={image.image_id === primaryImage?.image_id || loading}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.9)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                      }}
                    >
                      {image.image_id === primaryImage?.image_id ? (
                        <StarIcon color="primary" fontSize="small" />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete image">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteImage(image.image_id)}
                      disabled={loading || images.length === 1}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.9)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Card>
          </Box>
        ))}
      </Box>

      {/* Add Image Dropzone */}
      {editable && (
        <Box sx={{ mb: 2 }}>
          <Paper
            {...getRootProps()}
            data-local-dropzone
            sx={{
              height: 120,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
            }}
          >
              <input {...getInputProps()} />
              {uploading ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    {isDragActive
                      ? 'Drop images here'
                      : (images.length === 0 ? 'Add photos' : 'Add more photos')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ opacity: 0.7, mt: 0.25 }}>
                    {isDragActive ? '' : 'select one or many'}
                  </Typography>
                </>
              )}
          </Paper>
        </Box>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemImageGallery;
